import { getUtmObject } from 'reactApp/appHelpers/settingsHelpers';
import { BillingDataSelectors } from 'reactApp/modules/billingData/billingData.selectors';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getAccounts } from 'reactApp/modules/ph/ph.helpers';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { store as reduxStore } from 'reactApp/store';
import { sendDwh } from 'reactApp/utils/ga';

export const enum DwhPlace {
    files = 'files',
    public = 'public',
    favorites = 'favorites',
    joint = 'joint',
    search = 'search',
    fromMail = 'from_mail',
    trashbin = 'trashbin',
    mailDocuments = 'mail_documents',
    recommend = 'recommend',
    recent = 'recent',
    gallery = 'gallery',
    albums = 'albums',
    subscriptions = 'subscriptions',
    external = 'external',
    promoQuota = 'promo_quota',
    family = 'family',
    templates = 'templates',
    vigbo = 'vigbo',
}

/* eslint-disable sonarjs/max-switch-cases, no-fallthrough */

/**
 * !!WARNING!! – мапал как чувствовал – нужно проверить при тестировании
 * Получает place по storage
 * @param storage - сторадж
 * @returns поле place для аналитики
 */
const mapStorageToPlace = (storage: EStorageType): DwhPlace | undefined => {
    switch (storage) {
        // Стодаджа, которые не связаны с роутингом
        case EStorageType.archive:
        case EStorageType.story:
        // Конец

        case EStorageType.start:
        case EStorageType.home:
        case EStorageType.safe:
            return DwhPlace.files;

        case EStorageType.public:
        case EStorageType.sharingAttach:
            return DwhPlace.public;

        case EStorageType.favorites:
            return DwhPlace.favorites;

        case EStorageType.sharedIncoming:
        case EStorageType.sharedLinks:
        case EStorageType.sharedAutodelete:
        case EStorageType.incomingPublic:
            return DwhPlace.joint;

        case EStorageType.search:
            return DwhPlace.search;

        case EStorageType.attaches:
        case EStorageType.viewerAttaches:
        case EStorageType.stock:
        case EStorageType.myofficeAttaches:
        case EStorageType.attachesViewerDocuments:
        case EStorageType.attachesViewerError:
            return DwhPlace.fromMail;

        case EStorageType.trashbin:
            return DwhPlace.trashbin;

        case EStorageType.editor:
        case EStorageType.r7:
        case EStorageType.alldocuments:
        case EStorageType.pdfEdit:
        case EStorageType.r7wopi:
        case EStorageType.documents:
        case EStorageType.myoffice:
            return DwhPlace.mailDocuments;

        case EStorageType.recommend:
            return DwhPlace.recommend;

        case EStorageType.feed:
            return DwhPlace.recent;

        case EStorageType.gallery:
            return DwhPlace.gallery;

        case EStorageType.albums:
            return DwhPlace.albums;

        case EStorageType.subscriptions:
            return DwhPlace.subscriptions;

        case EStorageType.quotaCleaner:
        case EStorageType.quotaTariffs:
        case EStorageType.quotaPartner:
        case EStorageType.promocodes:
            return DwhPlace.promoQuota;

        case EStorageType.embedded:
        case EStorageType.integration:
        case EStorageType.inlineIntegration:
            return DwhPlace.external;

        case EStorageType.family:
        case EStorageType.familyFolders:
        case EStorageType.familyAlbums:
        case EStorageType.familyLanding:
            return DwhPlace.family;

        case EStorageType.templates:
            return DwhPlace.templates;

        case EStorageType.vigbo:
            return DwhPlace.vigbo;
    }
};
/* eslint-enable sonarjs/max-switch-cases, no-fallthrough */

interface SendTemplatedDwhParams extends Record<string, any> {
    eventCategory: string;
    action: string;
    replacementSymbol?: string;
}

/**
 * !!WARNING!! – часть полей пока не реализована
 * Хелпер для отправки аналитики, поддерживающий базовую аналитику из нового шаблонного события
 * @link https://metida.mail.ru/project/cloud/event/12351/atomic_event/17595
 * @param params - параметры аналитики
 *
 * События должны быть названы в соответствии с инструкцией
 * @link https://confluence.vk.team/pages/viewpage.action?pageId=1514181593
 */
export const sendTemplatedDwh = async (params: SendTemplatedDwhParams) => {
    const state = reduxStore.getState();

    const storage = getCurrentStorage(state);
    const place = mapStorageToPlace(storage);
    const enter_source = 'direct';
    const multi_accounts_list = (await getAccounts()).map(({ email }) => email).join(',');

    const activeSubscription = BillingDataSelectors.hasActiveSubscription(state);
    const tariff_start_dt = activeSubscription?.start;
    const tariff_period = activeSubscription?.productPeriod;
    const tariff_quota = activeSubscription?.space.original;
    const is_paid_flg = !!activeSubscription;

    const quotaUsed = UserQuotaSelectors.getUsed(state);
    const cloud_quota_used = quotaUsed.cloud?.original;
    const mail_quota_used = quotaUsed.mail?.original;

    const totalQuota = UserQuotaSelectors.getTotal(state);
    const quota_size = totalQuota?.original;

    const utms = getUtmObject();

    const isPhone = EnvironmentSelectors.isPhone();
    const platform = isPhone ? 'touch' : 'web';

    const { eventCategory, action, replacementSymbol = '-', ...rest } = params;

    const dwhData = {
        place,
        enter_source,
        platform,
        multi_accounts_list,
        tariff_start_dt,
        tariff_period,
        tariff_quota,
        is_paid_flg,
        cloud_quota_used,
        mail_quota_used,
        quota_size,
        ...utms,
        ...rest,
    };

    sendDwh({
        eventCategory,
        action,
        dwhData,
        replacementSymbol,
    });
};

interface CreateDwhTemplatedSenderParams {
    eventCategory: string;
    replacementSymbol?: string;
}

interface TemplatedDwhSenderParams extends Record<string, any> {
    action: string;
}

/**
 * Создает функцию отправки аналитики с конкретным t параметром
 * @param params - параметры создания
 * @param params.eventCategory - параметр t аналитики
 * @param params.replacementSymbol - символ для замены недопустимых символов
 * @returns - функцию отправки аналитики для конкретного t
 *
 * См. так же описание sendTemplatedDwh
 */
export const createDwhTemplatedSender = ({ eventCategory, replacementSymbol = '-' }: CreateDwhTemplatedSenderParams) => {
    return (params: TemplatedDwhSenderParams) => {
        sendTemplatedDwh({ ...params, eventCategory, replacementSymbol });
    };
};
